"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { BarChart } from "@/components/tools/stats-distributions/charts";
import {
  DistributionTable,
  ProbabilitySummary,
} from "@/components/tools/stats-distributions/discrete-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { binomialPmf } from "@/lib/stats/distributions";
import { barGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import {
  type DiscreteModel,
  discreteProbabilities,
  distributionTable,
  hypergeometricModel,
  tableRange,
} from "@/lib/tool-kit/stats-distributions/discrete";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";

/** English strings. A translated content file overrides these through `ui`. */
export const HYPERGEOMETRIC_DISTRIBUTION_CALCULATOR_STRINGS = {
  populationLabel: "Population size (N)",
  successesLabel: "Successes in the population (K)",
  drawsLabel: "Number drawn — sample size (n)",
  observedLabel: "Successes in the sample (k)",
  k2Label: "Upper end of a range (optional)",
  k2Hint: "Adds P(k ≤ X ≤ this value) to the results.",
  results: "Results",
  probability: "Probability",
  meaning: "Meaning",
  value: "Value",
  equal: "exactly {k}",
  atMost: "at most {k}",
  lessThan: "fewer than {k}",
  atLeast: "at least {k}",
  greaterThan: "more than {k}",
  between: "from {a} to {b}, inclusive",
  mean: "Mean (nK/N)",
  variance: "Variance",
  sd: "Standard deviation",
  supportLine: "k can only take values from {min} to {max} with these inputs.",
  withReplacement:
    "Drawn with replacement instead, the count would be binomial with n = {n} and p = K/N = {p}, giving P(X = {k}) = {binomial} against {exact} here. The finite population correction (N − n)/(N − 1) = {fpc} is why the variance here is smaller.",
  chartHeading: "Probability distribution",
  chartLabel:
    "Bar chart of P(X = k) for k from {from} to {to}, with the bars your question covers drawn darker",
  tableCaption: "Hypergeometric probabilities for N = {N}, K = {K}, n = {n}",
  tableLimit:
    "The table and chart are drawn while the distribution fits in about 200 rows.",
  enterPopulation:
    "Enter the population size as a whole number from 1 to 100,000.",
  enterSuccesses:
    "Enter the number of successes in the population as a whole number from 0 to N.",
  enterDraws: "Enter the number drawn as a whole number from 1 to N.",
  enterK: "Enter k as a whole number of 0 or more.",
} as const;

type Strings = typeof HYPERGEOMETRIC_DISTRIBUTION_CALCULATOR_STRINGS;

/** The cumulative sums walk the support term by term; this keeps them instant. */
const MAX_POPULATION = 100_000;

interface Inputs {
  population: number;
  successes: number;
  draws: number;
  k: number;
  k2: number | null;
}

interface Texts {
  population: string;
  successes: string;
  draws: string;
  k: string;
  k2: string;
}

function readInputs(texts: Texts): Inputs | keyof Strings {
  const population = parseIntegerInput(texts.population);
  if (population === null || population < 1 || population > MAX_POPULATION) {
    return "enterPopulation";
  }
  const successes = parseIntegerInput(texts.successes);
  if (successes === null || successes < 0 || successes > population) {
    return "enterSuccesses";
  }
  const draws = parseIntegerInput(texts.draws);
  if (draws === null || draws < 1 || draws > population) {
    return "enterDraws";
  }
  const k = parseIntegerInput(texts.k);
  const hasSecond = texts.k2.trim() !== "";
  const k2 = hasSecond ? parseIntegerInput(texts.k2) : null;
  if (k === null || k < 0 || (hasSecond && (k2 === null || k2 < 0))) {
    return "enterK";
  }
  return { population, successes, draws, k, k2 };
}

function Comparison({
  inputs,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { population, successes, draws, k } = inputs;
  const p = successes / population;
  const four = (value: number) =>
    fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  const fpc = population > 1 ? (population - draws) / (population - 1) : 0;
  return (
    <Notice>
      {t(strings.withReplacement, {
        n: fmt(draws),
        p: four(p),
        k: fmt(k),
        binomial: four(binomialPmf(k, draws, p)),
        exact: four(model.pmf(k)),
        fpc: four(fpc),
      })}
    </Notice>
  );
}

function Distribution({
  inputs,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { k, k2 } = inputs;
  const range = useMemo(
    () => tableRange(model, k2 === null ? [k] : [k, k2]),
    [model, k, k2]
  );
  const rows = useMemo(
    () => (range ? distributionTable(model, range[0], range[1]) : []),
    [model, range]
  );
  const geometry = useMemo(
    () => barGeometry(rows.map((row) => ({ k: row.k, p: row.pmf }))),
    [rows]
  );
  if (!range) {
    return <Notice>{strings.tableLimit}</Notice>;
  }
  const low = k2 === null ? k : Math.min(k, k2);
  const high = k2 === null ? k : Math.max(k, k2);
  const highlighted = (value: number) => value >= low && value <= high;
  return (
    <>
      <div className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
        <BarChart
          format={(value) => fmt(value)}
          geometry={geometry}
          highlighted={highlighted}
          label={t(strings.chartLabel, {
            from: fmt(range[0]),
            to: fmt(range[1]),
          })}
        />
      </div>
      <DistributionTable
        caption={t(strings.tableCaption, {
          N: fmt(inputs.population),
          K: fmt(inputs.successes),
          n: fmt(inputs.draws),
        })}
        fmt={fmt}
        highlighted={highlighted}
        rows={rows}
      />
    </>
  );
}

const FIELDS = [
  { key: "population", id: "hg-population", label: "populationLabel" },
  { key: "successes", id: "hg-successes", label: "successesLabel" },
  { key: "draws", id: "hg-draws", label: "drawsLabel" },
  { key: "k", id: "hg-k", label: "observedLabel" },
] as const;

export default function HypergeometricDistributionCalculatorWidget() {
  const strings = useToolUi(HYPERGEOMETRIC_DISTRIBUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [texts, setTexts] = useState<Texts>({
    population: "52",
    successes: "4",
    draws: "5",
    k: "2",
    k2: "",
  });
  const update = (patch: Partial<Texts>) =>
    setTexts((current) => ({ ...current, ...patch }));

  const inputs = readInputs(texts);
  const valid = typeof inputs === "object";
  const population = valid ? inputs.population : 1;
  const successes = valid ? inputs.successes : 0;
  const draws = valid ? inputs.draws : 1;
  const model = useMemo(
    () => hypergeometricModel(population, successes, draws),
    [population, successes, draws]
  );
  const four = (value: number) => fmt(value, { maximumFractionDigits: 4 });

  return (
    <>
      <section className="grid gap-4 sm:grid-cols-2">
        {FIELDS.map((field) => (
          <TextField
            id={field.id}
            key={field.key}
            label={strings[field.label]}
            numeric
            onChange={(value) =>
              update({ [field.key]: value } as Partial<Texts>)
            }
            value={texts[field.key]}
          />
        ))}
        <TextField
          hint={strings.k2Hint}
          id="hg-k2"
          label={strings.k2Label}
          numeric
          onChange={(k2) => update({ k2 })}
          value={texts.k2}
        />
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof inputs === "string" ? (
          <Notice>{strings[inputs]}</Notice>
        ) : (
          <>
            <ProbabilitySummary
              fmt={fmt}
              k={inputs.k}
              labels={strings}
              probabilities={discreteProbabilities(model, inputs.k, inputs.k2)}
            />
            <p className="text-muted-foreground text-sm">
              {t(strings.supportLine, {
                min: fmt(model.support[0]),
                max: fmt(model.support[1]),
              })}
            </p>
            <StatGrid>
              <StatCard label={strings.mean} value={four(model.mean)} />
              <StatCard label={strings.variance} value={four(model.variance)} />
              <StatCard
                label={strings.sd}
                value={four(Math.sqrt(model.variance))}
              />
            </StatGrid>
            <Comparison
              fmt={fmt}
              inputs={inputs}
              model={model}
              strings={strings}
            />
            <Distribution
              fmt={fmt}
              inputs={inputs}
              model={model}
              strings={strings}
            />
          </>
        )}
      </section>
    </>
  );
}
